import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import './SuperAdminDashboard.css';
import './SuperAdminMobileMenu.css';

function SuperAdminDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        menuRef.current &&
        toggleRef.current &&
        !menuRef.current.contains(event.target) &&
        !toggleRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);
  
  // Statistics state
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeClasses: 0,
    assignments: 0,
    completed: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  
  // School form state
  const [schoolForm, setSchoolForm] = useState({
    schoolName: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    principalName: '',
    principalEmail: '',
    schoolType: 'public',
    totalStudents: '',
    totalTeachers: '',
    website: '',
    description: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSchoolForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!schoolForm.schoolName.trim()) errors.schoolName = 'School name is required';
    if (!schoolForm.email.trim()) errors.email = 'Email is required';
    if (!schoolForm.password) errors.password = 'Password is required';
    if (schoolForm.password !== schoolForm.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (!schoolForm.address.trim()) errors.address = 'Address is required';
    if (!schoolForm.city.trim()) errors.city = 'City is required';
    if (!schoolForm.state.trim()) errors.state = 'State is required';
    if (!schoolForm.phone.trim()) errors.phone = 'Phone number is required';
    if (!schoolForm.principalName.trim()) errors.principalName = 'Principal name is required';
    if (!schoolForm.principalEmail.trim()) errors.principalEmail = 'Principal email is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Here you would typically save to your database
      console.log('School data to be saved:', schoolForm);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert('School added successfully!');
      
      // Reset form
      setSchoolForm({
        schoolName: '',
        email: '',
        password: '',
        confirmPassword: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        principalName: '',
        principalEmail: '',
        schoolType: 'public',
        totalStudents: '',
        totalTeachers: '',
        website: '',
        description: ''
      });
      
    } catch (error) {
      console.error('Error adding school:', error);
      alert('Error adding school. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Sign out from Supabase if logged in
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.auth.signOut();
      }
      
      // Clear localStorage
      localStorage.removeItem('isSuperAdminAuthenticated');
      
      // Redirect to home page
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear localStorage and redirect even if there's an error
      localStorage.removeItem('isSuperAdminAuthenticated');
      navigate('/');
    }
  };

  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Fetch total users (students + teachers)
      const [studentsResult, teachersResult, classesResult, homeworkResult] = await Promise.all([
        supabase.from('students').select('*', { count: 'exact', head: true }),
        supabase.from('teachers').select('*', { count: 'exact', head: true }),
        supabase.from('classes').select('*', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('homework').select('*', { count: 'exact', head: true })
      ]);
      
      const totalStudents = studentsResult.count || 0;
      const totalTeachers = teachersResult.count || 0;
      const totalUsers = totalStudents + totalTeachers;
      const activeClasses = classesResult.count || 0;
      const totalAssignments = homeworkResult.count || 0;
      
      // For completed, count homework that has passed due date
      const { count: completedCount } = await supabase
        .from('homework')
        .select('*', { count: 'exact', head: true })
        .lt('due_date', new Date().toISOString().split('T')[0]);
      
      setStats({
        totalUsers,
        activeClasses,
        assignments: totalAssignments,
        completed: completedCount || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchRecentActivities();
  }, []);

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' }
  ];
  
  // Format number with commas
  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
  };

  // Fetch recent activities
  const fetchRecentActivities = async () => {
    try {
      setActivitiesLoading(true);
      
      // Fetch recent activities from different tables
      const [studentsData, classesData, homeworkData, schoolsData] = await Promise.all([
        supabase
          .from('students')
          .select('id, first_name, last_name, created_at')
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('classes')
          .select('id, class_name, created_at')
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('homework')
          .select('id, title, created_at')
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('schools')
          .select('id, school_name, created_at')
          .order('created_at', { ascending: false })
          .limit(5)
      ]);

      // Combine all activities
      const activities = [];
      
      // Add student registrations
      if (studentsData.data && !studentsData.error) {
        studentsData.data.forEach(student => {
          const name = `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown User';
          activities.push({
            type: 'student',
            text: `New user registration: ${name}`,
            time: student.created_at
          });
        });
      }
      
      // Add class creations
      if (classesData.data && !classesData.error) {
        classesData.data.forEach(cls => {
          activities.push({
            type: 'class',
            text: `New class created: ${cls.class_name || 'Unknown Class'}`,
            time: cls.created_at
          });
        });
      }
      
      // Add homework assignments
      if (homeworkData.data && !homeworkData.error) {
        homeworkData.data.forEach(hw => {
          activities.push({
            type: 'homework',
            text: `Assignment created: ${hw.title || 'Untitled Assignment'}`,
            time: hw.created_at
          });
        });
      }
      
      // Add school additions
      if (schoolsData.data && !schoolsData.error) {
        schoolsData.data.forEach(school => {
          activities.push({
            type: 'school',
            text: `New school added: ${school.school_name || 'Unknown School'}`,
            time: school.created_at
          });
        });
      }
      
      // Sort by time (most recent first) and take top 10
      activities.sort((a, b) => new Date(b.time) - new Date(a.time));
      setRecentActivities(activities.slice(0, 10));
    } catch (error) {
      console.error('Error fetching recent activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="dashboard-content">
            <div className="welcome-message">
              <div className="welcome-card">
                <h2 style={{ color: '#000000', WebkitTextFillColor: '#000000' }} className="welcome-heading">Welcome, Super Administrator!</h2>
                <p>You have successfully accessed the Super Admin Portal. This is your central command center for managing the entire educational platform.</p>
                
                <div className="quick-stats">
                  <div className="stat-card" style={{ borderTop: '3px solid #FF8C42', borderTopColor: '#FF8C42', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexDirection: 'column' }}>
                    <div className="stat-icon-wrapper" style={{ width: '56px', height: '56px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg className="stat-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="stat-info" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                      <h3>TOTAL USERS</h3>
                      <p>{loading ? '...' : formatNumber(stats.totalUsers)}</p>
                    </div>
                  </div>
                  
                  <div className="stat-card" style={{ borderTop: '3px solid #FF8C42', borderTopColor: '#FF8C42', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexDirection: 'column' }}>
                    <div className="stat-icon-wrapper" style={{ width: '56px', height: '56px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg className="stat-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 10V15C22 20 20 22 15 22H9C4 22 2 20 2 15V9C2 4 4 2 9 2H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 10H18C15 10 14 9 14 6V2L22 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7 13H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7 17H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="stat-info" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                      <h3>ACTIVE CLASSES</h3>
                      <p>{loading ? '...' : formatNumber(stats.activeClasses)}</p>
                    </div>
                  </div>
                  
                  <div className="stat-card" style={{ borderTop: '3px solid #FF8C42', borderTopColor: '#FF8C42', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexDirection: 'column' }}>
                    <div className="stat-icon-wrapper" style={{ width: '56px', height: '56px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg className="stat-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="stat-info" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                      <h3>ASSIGNMENTS</h3>
                      <p>{loading ? '...' : formatNumber(stats.assignments)}</p>
                    </div>
                  </div>
                  
                  <div className="stat-card" style={{ borderTop: '3px solid #FF8C42', borderTopColor: '#FF8C42', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexDirection: 'column' }}>
                    <div className="stat-icon-wrapper" style={{ width: '56px', height: '56px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg className="stat-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="stat-info" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                      <h3>COMPLETED</h3>
                      <p>{loading ? '...' : formatNumber(stats.completed)}</p>
                    </div>
                  </div>
                </div>
                
                <div className="recent-activity">
                  <h3>Recent Activity</h3>
                  <div className="activity-list">
                    {activitiesLoading ? (
                    <div className="activity-item">
                        <span className="activity-text">Loading activities...</span>
                    </div>
                    ) : recentActivities.length === 0 ? (
                    <div className="activity-item">
                        <span className="activity-text">No recent activity</span>
                    </div>
                    ) : (
                      recentActivities.map((activity, index) => (
                        <div 
                          key={`${activity.type}-${activity.time}-${index}`} 
                          className={`activity-item ${index === recentActivities.length - 1 ? 'last-item' : ''}`}
                        >
                          <span className="activity-time">{formatTimeAgo(activity.time)}</span>
                          <span className="activity-text">{activity.text}</span>
                    </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="coming-soon">
            <h1>{sidebarItems.find(item => item.id === activeTab)?.label}</h1>
            <div className="coming-soon-content">
              <div className="coming-soon-icon">CS</div>
              <h2>Coming Soon</h2>
              <p>This section is under development. You'll be able to access {sidebarItems.find(item => item.id === activeTab)?.label.toLowerCase()} features here soon.</p>
              <div className="feature-preview">
                <h3>Planned Features:</h3>
                <ul>
                  <li>Advanced management tools</li>
                  <li>Detailed analytics and reporting</li>
                  <li>System configuration options</li>
                  <li>User activity monitoring</li>
                </ul>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="super-admin-dashboard">
      {/* Mobile Header */}
      <div className="mobile-header">
        <div className="mobile-header-left">
          <span className="mobile-header-text">Super Admin</span>
        </div>
        <button 
          ref={toggleRef}
          className="mobile-menu-toggle"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Toggle clicked, current state:', mobileMenuOpen);
            const newState = !mobileMenuOpen;
            setMobileMenuOpen(newState);
            console.log('Setting state to:', newState);
          }}
          aria-label="Toggle menu"
          type="button"
        >
          <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Sidebar - Mobile Dropdown Menu */}
      <div 
        ref={menuRef} 
        className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}
      >
        <div className="sidebar-header">
          <img 
            src="https://gjamvfwvqlizginqdfhd.supabase.co/storage/v1/object/public/transport-uploads/WhatsApp%20Image%202025-11-21%20at%2011.57.28%20AM%20(1).jpeg" 
            alt="Nexy App Logo" 
            className="sidebar-logo"
          />
        </div>
        
        <nav className="sidebar-nav">
          <button
            className="nav-item active"
            onClick={() => {
              setActiveTab('dashboard');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Dashboard</span>
          </button>
          
          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/add-school');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Add School</span>
          </button>
          
          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/schools');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Schools</span>
          </button>

          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/terms-and-conditions');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Terms & Conditions</span>
          </button>

          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/privacy-policy');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Privacy Policy / EULA</span>
          </button>
        </nav>
        
        <div className="sidebar-spacer"></div>
        
        <div className="sidebar-footer">
          <button 
            className="logout-btn" 
            onClick={() => {
              handleLogout();
              setMobileMenuOpen(false);
            }}
          >
            <span>Logout</span>
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h1>Dashboard</h1>
          <div className="user-info">
            <span>Super Admin</span>
            <div className="user-avatar" style={{ backgroundColor: '#FF8C42', background: '#FF8C42' }}>SA</div>
          </div>
        </div>
        
        <div className="content-body">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default SuperAdminDashboard;
